/*
 * Sign in, two-step, texted codes and sessions.
 */
import { z } from "zod";
import { phoneSchema } from "./api.zod.js";

/**
 * web gets an httpOnly cookie; mobile (the phone app) gets the token in the answer and keeps it in its secure store. claude
 * is a Claude connector (apps/api claude/): made only by Connect Claude, held by Claude, and locked to one company.
 */
export const sessionKind = z.enum(["web", "mobile", "claude"]);
export type SessionKind = z.infer<typeof sessionKind>;
/** The kinds a person signs in as. A claude session is never made by signing in. */
const signInKind = z.enum(["web", "mobile"]);

export const signInBody = z.object({
  email: z.email().trim().toLowerCase(),
  password: z.string().min(1).max(200),
  kind: signInKind.default("web"),
});

/** What sign in answers. twoStep says what comes next: nothing, a code, or setting up an authenticator app first. */
export const signInAnswer = z.object({
  twoStep: z.enum(["done", "needed", "setup"]),
  token: z.string().optional(),
});

export const twoStepBody = z.object({ code: z.string().trim().min(6).max(20) });

export const twoStepSetupAnswer = z.object({ secret: z.string(), otpauthUrl: z.string() });
export const backupCodesAnswer = z.object({ backupCodes: z.array(z.string()) });

export const codeRequestBody = z.object({ phone: phoneSchema });
export const codeVerifyBody = z.object({
  phone: phoneSchema,
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/),
  kind: signInKind.default("mobile"),
});

export const passwordBody = z.object({
  current: z.string().min(1).max(200),
  next: z.string().min(10, "Use at least 10 characters.").max(200),
});

/** The language this login reads the crew screens and its own messages in. */
export const languageBody = z.object({ locale: z.enum(["en", "es"]) });

export const forgotBody = z.object({ email: z.email().trim().toLowerCase() });
export const resetBody = z.object({
  token: z.string().trim().min(20).max(100),
  password: z.string().min(10, "Use at least 10 characters.").max(200),
});

/**
 * Switching to a company at another brand's address: the signed-in tab asks for a one-time link (url is null when the
 * company lives at this same address), and the new tab trades the code from it for its own session.
 */
export const handoffBody = z.object({ companyId: z.uuid() });
export const handoffAnswer = z.object({ url: z.string().nullable() });
export const handoffUseBody = z.object({
  code: z.string().trim().max(100),
  /** Shared sign in: the page to go back to after signing in here (one of this server's own addresses). */
  back: z.string().max(2000).optional(),
});
export const handoffUseAnswer = z.object({ companyId: z.uuid(), back: z.string().nullable() });

/**
 * Shared sign in across the brands' addresses (auth/sso.ts on the server). seed: right after signing in, a link that signs
 * in at head office's address too and comes back (null when there's nothing to do). ssoQuery: the page at a brand's address
 * that has no session yet.
 */
export const ssoSeedBody = z.object({ back: z.string().max(2000) });
export const ssoQuery = z.object({ to: z.string().max(2000) });

export const sessionRow = z.object({
  id: z.uuid(),
  kind: sessionKind,
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  createdAt: z.string(),
  lastUsedAt: z.string(),
  current: z.boolean(),
});

export const meAnswer = z.object({
  id: z.uuid(),
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  locale: z.enum(["en", "es"]),
  twoStepOn: z.boolean(),
  /** Backup codes not used yet. Logins moved from the WordPress Steward start at 0 and make new ones in Security. */
  backupCodesLeft: z.number().int(),
  companies: z.array(z.object({ id: z.uuid(), name: z.string(), role: z.string() })),
});
